import React, { useState, useEffect } from 'react';
import { CurrencySettings, loadCurrencySettings, saveCurrencySettings, DEFAULT_CURRENCY_SETTINGS, CURRENCY_SYMBOLS, convertCurrencyWithMarkup, getExchangeRate } from '../../utils/CurrencyUtils';
import './CurrencyManagement.css';

const CurrencyManagement: React.FC = () => {
  const [settings, setSettings] = useState<CurrencySettings>(DEFAULT_CURRENCY_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Load settings on component mount
    const loadedSettings = loadCurrencySettings();
    setSettings(loadedSettings);
  }, []);

  const updateSetting = (currency: string, field: keyof typeof settings[string], value: any) => {
    const newSettings = {
      ...settings,
      [currency]: {
        ...settings[currency],
        [field]: value
      }
    };
    
    setSettings(newSettings);
    
    // Save immediately and dispatch event for real-time updates
    saveCurrencySettings(newSettings);
    console.log('🚀 Dispatching currency_settings_updated event with:', newSettings);
    window.dispatchEvent(new CustomEvent('currency_settings_updated', { 
      detail: newSettings 
    }));
    console.log('✅ Event dispatched successfully');
  };

  // Enhanced Preview Component with Exchange Rates
  const CurrencyPreview: React.FC<{ currency: string; baseAmount?: number }> = ({ currency, baseAmount = 1000 }) => {
    const [previewData, setPreviewData] = useState<{
      baseAmount: number;
      markupAmount: number;
      roundedAmount: number;
      exchangeRate: number;
      markupPercentage: number;
      roundToNearest?: number;
      loading: boolean;
    }>({
      baseAmount: 0,
      markupAmount: 0,
      roundedAmount: 0,
      exchangeRate: 0,
      markupPercentage: 0,
      loading: true
    });

    useEffect(() => {
      const calculatePreview = async () => {
        setPreviewData(prev => ({ ...prev, loading: true }));
        try {
          // Get base exchange rate
          const baseRate = await getExchangeRate('R', currency);
          
          // Calculate base amount (no markup)
          const baseAmountConverted = baseAmount * baseRate;
          
          // Get markup settings
          const markupConfig = settings[currency];
          let markupAmountConverted = baseAmountConverted;
          let markupPercentage = 0;
          
          // Apply markup if configured
          if (markupConfig && markupConfig.enabled && markupConfig.markupPercentage > 0) {
            const markupMultiplier = 1 + (markupConfig.markupPercentage / 100);
            markupAmountConverted = baseAmountConverted * markupMultiplier;
            markupPercentage = markupConfig.markupPercentage;
          }
          
          // Calculate rounded amount (round UP)
          let roundedAmount = markupAmountConverted;
          if (markupConfig && markupConfig.enabled && markupConfig.roundToNearest) {
            const roundTo = markupConfig.roundToNearest;
            roundedAmount = Math.ceil(markupAmountConverted / roundTo) * roundTo;
          }
          
          setPreviewData({
            baseAmount: Math.round(baseAmountConverted * 100) / 100,
            markupAmount: Math.round(markupAmountConverted * 100) / 100,
            roundedAmount: Math.round(roundedAmount * 100) / 100,
            exchangeRate: baseRate,
            markupPercentage,
            roundToNearest: markupConfig?.roundToNearest,
            loading: false
          });
        } catch (error) {
          console.error('Error calculating preview:', error);
          setPreviewData(prev => ({ ...prev, loading: false }));
        }
      };
      
      calculatePreview();
    }, [currency, baseAmount, settings[currency]?.markupPercentage, settings[currency]?.roundToNearest, settings[currency]?.enabled]);

    if (previewData.loading) {
      return (
        <div className="preview-example">
          <div className="preview-row">
            <span>Loading...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="preview-example">
        {/* Exchange Rate Info */}
        <div className="preview-row exchange-rate-info">
          <span><strong>Exchange Rate:</strong> R 1.00 = {CURRENCY_SYMBOLS[currency]} {previewData.exchangeRate.toFixed(4)}</span>
        </div>
        <div className="preview-row exchange-rate-info">
          <span><strong>Reverse Rate:</strong> {CURRENCY_SYMBOLS[currency]} 1.00 = R {(1 / previewData.exchangeRate).toFixed(4)}</span>
        </div>
        
        {/* Base Conversion */}
        <div className="preview-row">
          <span>Base conversion (R {baseAmount}):</span>
          <span className="preview-amount">{CURRENCY_SYMBOLS[currency]} {previewData.baseAmount.toFixed(2)}</span>
        </div>
        
        {/* With Markup */}
        {previewData.markupPercentage > 0 && (
          <div className="preview-row">
            <span>With {previewData.markupPercentage}% markup:</span>
            <span className="preview-amount">{CURRENCY_SYMBOLS[currency]} {previewData.markupAmount.toFixed(2)}</span>
          </div>
        )}
        
        {/* With Rounding */}
        {previewData.roundToNearest && (
          <div className="preview-row">
            <span>Rounded UP to nearest {previewData.roundToNearest}:</span>
            <span className="preview-amount">{CURRENCY_SYMBOLS[currency]} {previewData.roundedAmount.toFixed(2)}</span>
          </div>
        )}
      </div>
    );
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);

    try {
      saveCurrencySettings(settings);
      setMessage({ type: 'success', text: 'Currency settings saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save currency settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_CURRENCY_SETTINGS);
    setMessage({ type: 'success', text: 'Settings reset to defaults!' });
    setTimeout(() => setMessage(null), 3000);
  };

  const currencies = Object.keys(DEFAULT_CURRENCY_SETTINGS);

  return (
    <div className="currency-management">
      <div className="currency-header">
        <h2>Currency</h2>
        <p>Configure markup and rounding for foreign currency pricing</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="currency-settings">
        {currencies.map(currency => (
          <div key={currency} className="currency-setting-card">
            <div className="currency-header">
              <div className="currency-info">
                <span className="currency-symbol">{CURRENCY_SYMBOLS[currency]}</span>
                <h3>{currency}</h3>
                <span className="currency-name">
                  {currency === 'R' ? 'South African Rand' :
                   currency === 'USD' ? 'US Dollar' :
                   currency === 'EUR' ? 'Euro' :
                   currency === 'GBP' ? 'British Pound' :
                   currency === 'CAD' ? 'Canadian Dollar' :
                   currency === 'AUD' ? 'Australian Dollar' : currency}
                </span>
              </div>
              <div className="currency-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings[currency]?.enabled || false}
                    onChange={(e) => updateSetting(currency, 'enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {settings[currency]?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            {settings[currency]?.enabled && (
              <div className="currency-config">
                <div className="config-row">
                  <label className="config-label">
                    <span className="label-text">Markup Percentage</span>
                    <span className="label-description">
                      Additional percentage added to exchange rate (e.g., 15% for processing fees)
                    </span>
                  </label>
                  <div className="config-input">
                    <input
                      type="number"
                      value={settings[currency]?.markupPercentage || 0}
                      onChange={(e) => updateSetting(currency, 'markupPercentage', parseInt(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="input-suffix">%</span>
                  </div>
                </div>

                <div className="config-row">
                  <label className="config-label">
                    <span className="label-text">Round to Nearest</span>
                    <span className="label-description">
                      Round final price UP to nearest value (leave empty for no rounding)
                    </span>
                  </label>
                  <div className="config-input">
                    <select
                      value={settings[currency]?.roundToNearest || ''}
                      onChange={(e) => updateSetting(currency, 'roundToNearest', e.target.value ? parseInt(e.target.value) : undefined)}
                    >
                      <option value="">No rounding</option>
                      <option value="1">1</option>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>

                <div className="config-preview">
                  <h4>Preview</h4>
                  <CurrencyPreview currency={currency} baseAmount={1000} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="currency-actions">
        <button 
          className="btn btn-secondary" 
          onClick={resetToDefaults}
          disabled={loading}
        >
          Reset to Defaults
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="currency-info">
        <h3>How it works</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>Automatic Detection</h4>
            <p>User's currency is automatically detected based on their IP location and browser settings.</p>
          </div>
          <div className="info-item">
            <h4>Markup Application</h4>
            <p>Configure markup percentages to cover processing fees, currency conversion costs, and other charges.</p>
          </div>
          <div className="info-item">
            <h4>Price Rounding</h4>
            <p>Round final prices UP to clean numbers (e.g., nearest $10) for better customer experience and to ensure you don't lose money on conversions.</p>
          </div>
          <div className="info-item">
            <h4>Real-time Updates</h4>
            <p>Changes are applied immediately across all package displays on your website.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyManagement;
